import type { CSSProperties } from 'react';
import { Button, Check } from '../../modules/design-system';

export interface MyPacksToolbarProps {
  totalSelectable: number;
  selectedCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  onShip: () => void;
  onSubmit: () => void;
  /** Bulk-sell in flight — show a spinner on Buyback and freeze the
   *  other controls so the user can't change the selection mid-request. */
  submitting?: boolean;
  /** When false the Ship affordances are hidden (operator disabled shipping
   *  for this session); Buyback stays available. */
  canShip?: boolean;
}

/**
 * Toolbar with two responsive layouts:
 *   • Mobile (Figma 278:7388) — sticky bottom bar pinned to the viewport,
 *     two equal-width pills: a glass-frosted "Select All" / "Ship" slot
 *     (label + handler swap on `selectedCount > 0`, so the same button
 *     gates the picker first and then ships the chosen set) + the
 *     primary "Buyback All" CTA.
 *   • ≥600px (Figma 706:1817) — inline row on the right side of the
 *     stats bar with Select All checkbox + Ship glass pill + Buyback
 *     primary CTA.
 */
export function MyPacksToolbar({
  totalSelectable,
  selectedCount,
  onSelectAll,
  onClear,
  onShip,
  onSubmit,
  submitting = false,
  canShip = true,
}: MyPacksToolbarProps) {
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable;
  const someSelected = selectedCount > 0;
  // Ship takes exactly one card: a delivery is started per card, so a
  // multi-card selection is a Buyback-only selection. The affordance stays
  // visible (with a reason on hover) instead of vanishing, so the player can
  // see why it is unavailable rather than wonder where it went.
  const canShipSelection = canShip && selectedCount === 1;
  const shipDisabledReason =
    canShip && selectedCount > 1 ? 'You can ship one card at a time' : undefined;

  return (
    <>
      {/* Desktop / wide tablet — inline row inside the stats bar. */}
      <div className="hidden @[600px]:flex items-center justify-end gap-[10px] flex-wrap @[400px]:flex-nowrap">
        <button
          type="button"
          onClick={allSelected ? onClear : onSelectAll}
          disabled={totalSelectable === 0 || submitting}
          data-co-component="MyPacksSelectAll"
          className="
            inline-flex items-center gap-2
            transition-opacity hover:opacity-90
            disabled:cursor-not-allowed disabled:opacity-40
          "
          style={{ color: 'var(--co-MyPacksSelectAll-textColor, #FFFFFF)' }}
        >
          <span
            aria-hidden
            className="inline-flex size-[20px] items-center justify-center [border-radius:var(--co-MyPacksSelectAll-borderRadius,7px)] transition-colors"
            style={{
              background: allSelected
                ? 'var(--co-MyPacksSelectAll-checkedFill, var(--co-MyPacksToolbarSubmit-fill, #23D11D))'
                : 'transparent',
              border: '1px solid var(--co-MyPacksSelectAll-boxBorder, rgba(222,222,224,0.29))',
              // Check icon strokes with `currentColor`.
              color: 'var(--co-MyPacksSelectAll-checkColor, #FFFFFF)',
            }}
          >
            {allSelected && <Check size={14} strokeWidth={3} />}
          </span>
          <span
            className="font-semibold whitespace-nowrap"
            style={{ fontSize: '13px', lineHeight: '20px' }}
          >
            Select All
          </span>
        </button>

        {canShip && (
          <>
            <span
              aria-hidden
              className="inline-block h-[20px] w-px shrink-0"
              style={{ background: '#EAEAEB', opacity: 0.1 }}
            />

            <Button
              variant="glass-frosted"
              size="md"
              onClick={onShip}
              disabled={!canShipSelection || submitting}
              title={shipDisabledReason}
              style={{ '--co-Button-glass-frostedBorderWidth': 0 } as CSSProperties}
            >
              Ship
            </Button>
          </>
        )}

        <Button
          variant="primary"
          size="md"
          onClick={onSubmit}
          disabled={!someSelected}
          loading={submitting}
        >
          Accept Buyback
        </Button>
      </div>

      {/* Mobile — fixed bottom bar with two equal-width pills.
          Sits ON the bottom edge and carries the host inset as extra BOTTOM
          PADDING instead of being lifted by it: the bar's frosted surface then
          continues behind the host's own navigation, while the buttons stay
          above it. Lifting the whole bar (the earlier `bottom-[var(--page-safe-
          bottom)]`) left a strip of page background between the surface and the
          host's tab bar — the same unfinished edge the pack rail had. The bar's
          top edge is unchanged either way, so nothing below needs re-reserving.
          `z-[45]` clears Backdrop's bottom inset cover (z-40), which fills that
          strip with the page background and would otherwise paint over us. */}
      <div
        className="
          @[600px]:hidden fixed bottom-0 left-0 right-0 z-[45]
          flex items-center gap-3 px-4 pt-3 pb-[calc(1rem+var(--page-safe-bottom,0px))]
        "
        style={{
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div className="flex-1">
          <Button
            variant="glass-frosted"
            size="lg"
            fullWidth
            onClick={someSelected && canShip ? onShip : onSelectAll}
            // In its Ship role the same one-card rule applies; in its Select
            // All role it only needs something to select.
            disabled={
              someSelected && canShip
                ? !canShipSelection || submitting
                : totalSelectable === 0 || submitting
            }
            title={shipDisabledReason}
          >
            {someSelected && canShip ? 'Ship' : 'Select All'}
          </Button>
        </div>
        <div className="flex-1">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={onSubmit}
            disabled={!someSelected}
            loading={submitting}
          >
            Buyback All
          </Button>
        </div>
      </div>
    </>
  );
}
